import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, BarChart3, Mic, FileText, Search, MessageCircle, LogOut, ExternalLink, Gift } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

type Counts = { pending: number; openSlots: number; posts: number; events: number; registrations: number };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [c, setC] = useState<Counts | null>(null);

  useEffect(() => {
    const now = new Date().toISOString();
    Promise.all([
      supabase.from("appointment_bookings").select("id", { count: "exact", head: true }).eq("status", "pending"),
      supabase.from("appointment_slots").select("id", { count: "exact", head: true }).eq("status", "open").gt("starts_at", now),
      supabase.from("blog_posts").select("id", { count: "exact", head: true }).eq("published", true),
      supabase.from("events").select("id", { count: "exact", head: true }).eq("published", true),
      supabase.from("event_registrations").select("id", { count: "exact", head: true }),
    ]).then(([a, b, p, e, r]) =>
      setC({ pending: a.count ?? 0, openSlots: b.count ?? 0, posts: p.count ?? 0, events: e.count ?? 0, registrations: r.count ?? 0 }),
    );
  }, []);

  const tiles = [
    { to: "/admin/termine", icon: CalendarCheck, title: "Termine verwalten", desc: "Freie Zeiten anlegen, Buchungen bestätigen", stat: c ? `${c.pending} offene Anfragen · ${c.openSlots} freie Termine` : "…", highlight: (c?.pending ?? 0) > 0 },
    { to: "/admin/blog", icon: FileText, title: "Magazin-Beiträge", desc: "Artikel schreiben und bearbeiten", stat: c ? `${c.posts} veröffentlicht` : "…" },
    { to: "/admin/blog/statistik", icon: BarChart3, title: "Magazin-Statistik", desc: "Welche Artikel wie oft gelesen werden", stat: "Aufrufe ansehen" },
    { to: "/admin/veranstaltungen", icon: Mic, title: "Veranstaltungen", desc: "Vorträge, Gäste und Anmeldungen", stat: c ? `${c.events} online · ${c.registrations} Anmeldungen` : "…" },
    { to: "/admin/advent", icon: Gift, title: "Adventkalender", desc: "Türchen bearbeiten, Lostopf & Verlosung", stat: "Öffnen" },
    { to: "/admin/mikrobi", icon: MessageCircle, title: "Mikrobi", desc: "Fragen der Besucher und Antworten", stat: "Auswertung" },
    { to: "/admin/seo", icon: Search, title: "Google & SEO", desc: "Sichtbarkeit in der Suche", stat: "Bericht" },
  ];

  const logout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-secondary">
      <div className="container mx-auto px-4 py-10 max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-sm text-muted-foreground">Praxis Innere Balance</p>
            <h1 className="text-3xl font-bold text-foreground">Willkommen zurück, Isabella</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href="/" target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" />Website</a>
            </Button>
            <Button variant="outline" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-1.5" />Abmelden</Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((t) => (
            <Link key={t.to} to={t.to} className="group">
              <Card className={`h-full p-6 transition-all group-hover:shadow-lg group-hover:-translate-y-0.5 ${t.highlight ? "border-primary" : ""}`}>
                <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <t.icon className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-semibold text-foreground group-hover:text-primary">{t.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
                <p className={`text-sm font-medium mt-4 ${t.highlight ? "text-primary" : "text-foreground"}`}>{t.stat}</p>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
